import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { ShieldCheck, ArrowUpRight, Check, Phone, Clock, Shield, Sparkles, ChevronDown, ChevronUp, Lock, Globe, EyeOff, DollarSign, Zap } from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Lock, title: 'Number anonymization', text: 'Mask phone numbers to anonymize voice calls, keeping personal numbers safe from exposure.' },
  { icon: Shield, title: 'Privacy protection', text: 'Protect privacy while maintaining communication. Both parties see masked numbers instead of real phone numbers.' },
  { icon: Globe, title: 'Global support', text: 'Number masking works across all supported countries and phone number types.' },
  { icon: Zap, title: 'Instant setup', text: 'Enable number masking instantly without complex configuration or technical setup.' },
];

const useCases = [
  { title: 'Marketplace platforms', text: 'Connect buyers and sellers without revealing personal phone numbers for safer transactions.' },
  { title: 'Delivery services', text: 'Allow customers and drivers to communicate while keeping both parties\' phone numbers private.' },
  { title: 'Dating apps', text: 'Enable voice calls between matches while protecting user privacy until they\'re ready to share real numbers.' },
  { title: 'Professional services', text: 'Consultants and freelancers can communicate with clients without exposing personal contact information.' },
];

const benefits = [
  { icon: ShieldCheck, title: 'Enhanced privacy', description: 'Protect personal phone numbers from being shared, stored, or misused by third parties.' },
  { icon: DollarSign, title: 'No extra cost', description: 'Number masking is included free with Zuri Voice - no per-call or setup fees.' },
  { icon: Clock, title: 'Instant activation', description: 'Enable masking on any phone number or call flow with a single click.' },
  { icon: Sparkles, title: 'Seamless experience', description: 'Calls work normally for both parties - they just see masked numbers instead of real ones.' },
];

const howItWorks = [
  {
    step: '1',
    title: 'Enable masking',
    description: 'Turn on number masking for specific phone numbers or call flows from your dashboard.',
    icon: Lock,
  },
  {
    step: '2',
    title: 'Make calls',
    description: 'When calls are made, both parties see a masked number instead of the real phone number.',
    icon: Phone,
  },
  {
    step: '3',
    title: 'Stay protected',
    description: 'Personal numbers remain private while communication continues normally.',
    icon: ShieldCheck,
  },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is number masking?',
    answer: 'Number masking (also called number anonymization) replaces real phone numbers with temporary or virtual numbers during calls. This allows two parties to communicate without revealing their actual phone numbers to each other.'
  },
  {
    question: 'Is number masking included in the pricing?',
    answer: 'Yes! Number masking is completely free with Zuri Voice. There are no additional charges for enabling or using number masking on your calls. You only pay for standard voice minutes.'
  },
  {
    question: 'How does number masking work?',
    answer: 'When masking is enabled, Zuri Voice assigns a virtual number to each call. Both parties see and call this virtual number instead of each other\'s real numbers. The system routes calls through the virtual number while keeping real numbers private.'
  },
  {
    question: 'Can I use number masking for all my calls?',
    answer: 'Yes, you can enable number masking for specific phone numbers, call flows, or all calls depending on your needs. This flexibility allows you to use masking where privacy is most important.'
  },
  {
    question: 'Does number masking affect call quality?',
    answer: 'No, number masking has no impact on call quality. Calls are routed through the same infrastructure with the same reliability and audio quality. The only difference is the phone numbers displayed to participants.'
  },
  {
    question: 'Can masked numbers receive SMS messages?',
    answer: 'Yes, masked numbers can send and receive SMS messages in addition to voice calls. This provides complete communication privacy across both voice and text channels.'
  },
  {
    question: 'How long do masked numbers remain active?',
    answer: 'Masked numbers can be configured for different timeframes depending on your use case. They can be temporary (for single transactions) or persistent (for ongoing relationships). You control the lifecycle.'
  },
  {
    question: 'Is number masking secure?',
    answer: 'Absolutely. Number masking uses encrypted connections and secure infrastructure to protect real phone numbers. Access to unmasked numbers is restricted to authorized administrators and logged for audit purposes.'
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function NumberMasking() {
  return (
    <>
      <SEO
        title="Number Masking - Privacy Protection | Zuri Voice"
        description="Mask phone numbers to anonymize voice calls, keeping personal numbers safe. Protect privacy while maintaining communication. Included free with Zuri Voice."
        path="/number-masking"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <ShieldCheck className="h-4 w-4" /> Included free
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Number masking that <span className="gradient-text">protects privacy</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Mask phone numbers to anonymize voice calls, keeping personal numbers safe. Protect privacy while maintaining communication. No extra cost.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/zuri-voice"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Back to Zuri Voice <ShieldCheck className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Number%20masking.png"
                  alt="Number masking dashboard showing privacy protection and anonymization features"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful privacy features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to protect phone number privacy.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="how-heading" className="section-title">How it works</h2>
            <p className="section-subtitle mx-auto mt-4">Simple setup, powerful protection.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {howItWorks.map((step) => (
              <div key={step.step} className="relative rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                  <step.icon className="h-8 w-8" />
                </div>
                <span className="absolute -top-3 left-6 rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">{step.step}</span>
                <h3 className="text-xl font-bold text-slate-900">{step.title}</h3>
                <p className="mt-3 text-sm text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why use number masking?</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that protect your users and business.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="security-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="security-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">Enterprise-grade security</h2>
              <p className="mt-4 text-slate-300">
                Number masking is built with security at its core, ensuring personal data remains protected at all times.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Shield,
                    title: 'Encrypted storage',
                    description: 'Real phone numbers are encrypted and stored securely with strict access controls.',
                  },
                  {
                    icon: EyeOff,
                    title: 'Audit logging',
                    description: 'All access to unmasked numbers is logged for compliance and security monitoring.',
                  },
                  {
                    icon: Lock,
                    title: 'Role-based access',
                    description: 'Only authorized administrators can view or manage unmasked numbers.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Compliance ready</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> GDPR compliant data handling</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> CCPA compliant privacy practices</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Data minimization principles</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Right to deletion support</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Secure data retention policies</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Regular security audits</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about number masking</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}